// # Filename: src/components/poker/shared/pokerGameUtils.js
//
// Pure, breakpoint-agnostic helpers for reading a poker game payload. No JSX, no
// sizing/positioning math -- those live in the tier-specific table components.

export function hasFaceUpCards(cards = []) {
  return cards.some((card) => card && card !== "??");
}

export function isEliminatedPlayer(player) {
  return Boolean(
    player &&
      Number(player.chips || 0) <= 0 &&
      Number(player.bet || 0) <= 0 &&
      !player.allIn &&
      !(player.cards || []).length
  );
}

export function playersFromGame(game) {
  if (!game) return [];
  if (game.players?.length) {
    return game.players.map((seat) => ({
      seat: seat.seat,
      name: seat.name || "Player",
      chips: seat.chips,
      bet: seat.bet,
      cards: seat.cards || [],
      best: seat.best,
      folded: seat.folded,
      allIn: seat.all_in,
    }));
  }
  return [
    {
      seat: 1,
      name: game.player_one_name || "Player 1",
      chips: game.player_one_chips,
      bet: game.player_one_bet,
      cards: game.player_one_cards || [],
      best: game.player_one_best,
      folded: game.player_one_folded,
      allIn: game.player_one_all_in,
    },
    {
      seat: 2,
      name: game.player_two_name || "Player 2",
      chips: game.player_two_chips,
      bet: game.player_two_bet,
      cards: game.player_two_cards || [],
      best: game.player_two_best,
      folded: game.player_two_folded,
      allIn: game.player_two_all_in,
    },
  ];
}

export function chipValueForSeat(game, seat) {
  const seatNumber = Number(seat);
  return Number(playersFromGame(game).find((player) => Number(player.seat) === seatNumber)?.chips || 0);
}

export function nextOccupiedSeat(fromSeat, players) {
  const ordered = [...players].map((player) => Number(player.seat)).sort((a, b) => a - b);
  if (!ordered.length) return null;
  const current = Number(fromSeat);
  return ordered.find((seat) => seat > current) || ordered[0];
}

export function getBlindSeats(game, players) {
  const dealer = Number(game?.dealer);
  if (!dealer || !players.length) return { smallBlindSeat: null, bigBlindSeat: null };
  if (players.length === 2) {
    return {
      smallBlindSeat: dealer,
      bigBlindSeat: nextOccupiedSeat(dealer, players),
    };
  }
  const smallBlindSeat = nextOccupiedSeat(dealer, players);
  return {
    smallBlindSeat,
    bigBlindSeat: nextOccupiedSeat(smallBlindSeat, players),
  };
}

export function optimisticActionLabel(action, amount, game) {
  switch (action) {
    case "check":
      return "Check";
    case "call":
      return `Call ${game?.call_amount || ""}`.trim();
    case "raise":
      return `Bet ${amount || ""}`.trim();
    case "all_in":
      return "All in";
    case "fold":
      return "Fold";
    default:
      return "";
  }
}

export function playerMoveLabel(player, active, game) {
  if (!player) return "";
  if (active && !game?.is_completed) return "Action";
  if (player.folded) return "Fold";
  if (player.allIn) return "All in";
  if (player.bet) return `Bet ${player.bet}`;
  return "Waiting";
}

export function actionLabelClass(label, active = false) {
  const normalized = String(label || "").toLowerCase();
  if (active || normalized === "action" || normalized === "to act") {
    return "bg-emerald-300 text-slate-950";
  }
  if (normalized.startsWith("bet") || normalized.startsWith("raise")) {
    return "bg-amber-300/18 text-amber-100";
  }
  if (normalized.startsWith("call") || normalized === "check") {
    return "bg-cyan-300/16 text-cyan-100";
  }
  if (normalized.includes("all in") || normalized.includes("all-in")) {
    return "bg-rose-400/20 text-rose-100";
  }
  if (normalized.startsWith("fold")) {
    return "bg-slate-950/60 text-slate-300";
  }
  return "bg-black/28 text-emerald-50/62";
}
